import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageHeader, Panel, Button, IconButton, Field, TextInput, TextArea, Select, Banner, Spinner, DataTable, Tag, EmptyState, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import GradeFormDialog from '../../components/GradeFormDialog.jsx';
import { formatHebrewDate } from '../../utils/hebrewDate';
import { FormGrid, Row, apiError } from '../../next/ui';

/**
 * כרטיס מבחן — new design. Same endpoints as ExamCard (/exams/:id, /exam-periods, /grades?examId=…),
 * library links (חלק + ranges of simanim) and the exam's grades with the grade dialog.
 */
const EMPTY_RANGE = { simanFrom: '', simanTo: '' };
const EMPTY_FORM = {
  code: '', name: '', type: 'time', subject: '', date: '', status: 'draft', kolelCode: '', city: '', periodId: '', graderNotes: '', introText: '', libraryLinks: [],
};
const TYPE_OPTIONS = [{ value: 'time', label: 'זמן' }, { value: 'shimur', label: 'שימור' }, { value: 'miktzoa', label: 'מקצוע' }];
const STATUS_OPTIONS = [{ value: 'draft', label: 'טיוטה' }, { value: 'open', label: 'פתוח' }, { value: 'closed', label: 'סגור' }, { value: 'graded', label: 'צויין' }];
const CHELEK_OPTIONS = [{ value: '', label: '—' }, ...['אורח חיים', 'יורה דעה', 'אבן העזר', 'חושן משפט'].map((v) => ({ value: v, label: v }))];
const normalizePeriodId = (p) => (!p ? '' : typeof p === 'object' ? p._id || '' : p);
const digits = (v) => String(v || '').replace(/\D/g, '');

export default function ExamCardNext({ recordId, onClose }) {
  const toast = useCkToast();
  const params = useParams();
  const navigate = useNavigate();
  const id = recordId || params.id;
  const isNew = !id || id === 'new';
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);

  useEffect(() => {
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      if (isNew) {
        const active = list.find((p) => p.isActive);
        if (active) setForm((f) => (f.periodId ? f : { ...f, periodId: active._id }));
      }
    }).catch(() => {});
  }, [isNew]);

  const fetchGrades = useCallback(async () => {
    if (isNew || !id) return;
    setGradesLoading(true);
    try {
      const { data } = await api.get('/grades', { params: { examId: id, all: 1 } });
      setGrades(data.data || []);
    } catch { /* keep */ } finally {
      setGradesLoading(false);
    }
  }, [id, isNew]);
  useEffect(() => { fetchGrades(); }, [fetchGrades]);

  const applyData = (data) => setForm({ ...EMPTY_FORM, ...data, date: data.date ? new Date(data.date).toISOString().slice(0, 10) : '', periodId: normalizePeriodId(data.periodId) });
  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    api.get(`/exams/${id}`).then(({ data }) => applyData(data)).catch(() => setError('שגיאה בטעינת נתוני מבחן')).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const updateLinks = (fn) => setForm((f) => ({ ...f, libraryLinks: fn(f.libraryLinks || []) }));
  const addLink = () => updateLinks((l) => [...l, { chelek: '', ranges: [{ ...EMPTY_RANGE }] }]);
  const removeLink = (li) => updateLinks((l) => l.filter((_, i) => i !== li));
  const changeChelek = (li, v) => updateLinks((l) => l.map((x, i) => (i === li ? { ...x, chelek: v } : x)));
  const addRange = (li) => updateLinks((l) => l.map((x, i) => (i === li ? { ...x, ranges: [...(x.ranges || []), { ...EMPTY_RANGE }] } : x)));
  const removeRange = (li, ri) => updateLinks((l) => l.map((x, i) => (i === li ? { ...x, ranges: x.ranges.filter((_, j) => j !== ri) } : x)));
  const changeRange = (li, ri, k, v) => updateLinks((l) => l.map((x, i) => (i === li ? { ...x, ranges: x.ranges.map((r, j) => (j === ri ? { ...r, [k]: v } : r)) } : x)));

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const payload = { ...form, code: form.code ? Number(form.code) : form.code, kolelCode: form.kolelCode ? Number(form.kolelCode) : null, periodId: form.periodId || null };
      if (isNew) {
        const { data } = await api.post('/exams', payload);
        toast.success('מבחן נוצר בהצלחה');
        if (onClose) onClose('created'); else navigate(`/exams/${data._id}`);
      } else {
        const { data } = await api.put(`/exams/${id}`, payload);
        applyData(data);
        toast.success('נשמר בהצלחה');
      }
    } catch (e) {
      setError(apiError(e, 'שגיאת שמירה'));
    } finally {
      setSaving(false);
    }
  };
  const back = () => (onClose ? onClose() : navigate('/exams'));
  const statusLabel = STATUS_OPTIONS.find((o) => o.value === form.status)?.label;

  if (loading) return <div className="ck-empty"><Spinner size={26} /></div>;

  return (
    <div className="nx-stack">
      {!recordId ? <button type="button" className="adm-back" onClick={back}><span aria-hidden="true">→</span>מבחנים</button> : null}
      <PageHeader
        title={isNew ? 'מבחן חדש' : form.name || 'מבחן'}
        sub={isNew ? 'כרטיס מבחן' : [form.code ? `קוד ${form.code}` : null, form.subject, statusLabel].filter(Boolean).join(' · ')}
        primary={{ label: saving ? 'שומר…' : 'שמירה', icon: 'check', onClick: save }}
      >
        <Button variant="ghost" onClick={back}>{recordId ? 'סגירה' : 'ביטול'}</Button>
      </PageHeader>
      {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}

      <Panel title="פרטי מבחן" icon="task">
        <FormGrid cols={3}>
          <Field label="קוד *"><TextInput type="number" ltr min={1} step={1} value={form.code || ''} onChange={(e) => setForm((f) => ({ ...f, code: digits(e.target.value) }))} data-autofocus /></Field>
          <Field label="שם מבחן *"><TextInput value={form.name || ''} onChange={set('name')} /></Field>
          <Field label="סוג *"><Select value={form.type || 'time'} onChange={set('type')} options={TYPE_OPTIONS} /></Field>
          <Field label="נושא *"><TextInput value={form.subject || ''} onChange={set('subject')} /></Field>
          <Field label="תאריך *"><TextInput type="date" value={form.date || ''} onChange={set('date')} /></Field>
          <Field label="סטטוס"><Select value={form.status || 'draft'} onChange={set('status')} options={STATUS_OPTIONS} /></Field>
          <Field label="תקופה"><Select value={form.periodId || ''} onChange={set('periodId')} placeholder="ללא תקופה" options={periods.map((p) => ({ value: p._id, label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))} /></Field>
          <Field label="קוד כולל"><TextInput ltr value={form.kolelCode || ''} onChange={set('kolelCode')} /></Field>
          <Field label="עיר" hint="משויכת אוטומטית לפי קוד כולל"><TextInput value={form.city || ''} readOnly /></Field>
          <div className="nx-span-all"><Field label="טקסט בתחילת המבחן" hint="לדוג׳: מי שיש לו שני מבחנים עונה רק על שאלות עם כוכבית"><TextArea rows={3} value={form.introText || ''} onChange={set('introText')} /></Field></div>
          <div className="nx-span-all"><Field label="הערות והארות (לבודק)" hint="מופיע בסוף דף הבחינה"><TextArea rows={3} value={form.graderNotes || ''} onChange={set('graderNotes')} /></Field></div>
        </FormGrid>
      </Panel>

      <Panel title="שיוך לספריית שאלות" sub="חלק בשו״ע וטווחי הסימנים שהמבחן כולל" icon="book" actions={<Button size="sm" icon="plus" onClick={addLink}>הוסף חלק</Button>}>
        {(form.libraryLinks || []).length === 0 ? (
          <EmptyState compact icon="book" title="לא הוגדרו שיוכים לספרייה">לחצו «הוסף חלק» כדי לשייך סימנים.</EmptyState>
        ) : (
          <div className="nx-list">
            {form.libraryLinks.map((link, li) => (
              <div key={li} className="nx-item" style={{ alignItems: 'flex-start' }}>
                <div className="nx-stack" style={{ flex: 1, gap: 8 }}>
                  <Row>
                    <div style={{ width: 180 }}><Select value={link.chelek || ''} onChange={(v) => changeChelek(li, v)} options={CHELEK_OPTIONS} aria-label="חלק" /></div>
                    <Button size="sm" variant="ghost" icon="plus" disabled={!link.chelek} onClick={() => addRange(li)}>רצף</Button>
                    <span className="ck-grow" />
                    <IconButton icon="trash" label="הסרת חלק" onClick={() => removeLink(li)} />
                  </Row>
                  {(link.ranges || []).map((range, ri) => (
                    <Row key={ri}>
                      <TextInput ltr type="number" min={1} style={{ width: 110 }} placeholder="מסימן" disabled={!link.chelek} value={range.simanFrom || ''} onChange={(e) => changeRange(li, ri, 'simanFrom', digits(e.target.value))} />
                      <span className="ck-muted">—</span>
                      <TextInput ltr type="number" min={1} style={{ width: 110 }} placeholder="עד סימן" disabled={!link.chelek} value={range.simanTo || ''} onChange={(e) => changeRange(li, ri, 'simanTo', digits(e.target.value))} />
                      {(link.ranges || []).length > 1 ? <IconButton size="sm" icon="x" label="הסרת רצף" onClick={() => removeRange(li, ri)} /> : null}
                    </Row>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {!isNew ? (
        <Panel title={`ציונים (${grades.length})`} icon="check" tone="success" flush actions={<Button size="sm" variant="primary" icon="plus" onClick={() => { setEditingGrade(null); setGradeDialogOpen(true); }}>הוסף ציון</Button>}>
          <DataTable
            mode="client"
            rows={grades}
            rowKey="_id"
            loading={gradesLoading}
            selectable={false}
            storageKey="bchinot-exam-grades"
            exportFilename={`ציונים_${form.code || ''}`}
            emptyTitle="לא הוזנו ציונים למבחן זה"
            onRowClick={(g) => { setEditingGrade(g); setGradeDialogOpen(true); }}
            rowActions={(g) => [{ key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => { setEditingGrade(g); setGradeDialogOpen(true); } }]}
            columns={[
              { key: 'name', label: 'שם', getValue: (g) => (g.memberId ? `${g.memberId.firstName || ''} ${g.memberId.lastName || ''}`.trim() : '—'), render: (g) => <b>{g.memberId ? `${g.memberId.firstName || ''} ${g.memberId.lastName || ''}`.trim() : '—'}</b> },
              { key: 'idNumber', label: 'ת״ז', ltr: true, getValue: (g) => g.memberId?.idNumber || '—' },
              { key: 'tractate', label: 'מסכת', getValue: (g) => g.tractate || '—' },
              { key: 'score', label: 'ציון', type: 'number', render: (g) => (g.score >= 90 ? <Tag tone="success">{g.score}</Tag> : g.score) },
              { key: 'source', label: 'מקור', type: 'enum', getValue: (g) => (g.source === 'ivr' ? 'טלפון' : 'ווב'), render: (g) => <Tag icon={g.source === 'ivr' ? 'phone' : 'grid'}>{g.source === 'ivr' ? 'טלפון' : 'ווב'}</Tag> },
              { key: 'enteredAt', label: 'תאריך', type: 'date', render: (g) => formatHebrewDate(g.enteredAt) || '—' },
            ]}
          />
        </Panel>
      ) : null}

      <GradeFormDialog
        open={gradeDialogOpen}
        onClose={() => { setGradeDialogOpen(false); setEditingGrade(null); }}
        exam={!isNew ? { _id: id, name: form.name, code: form.code, type: form.type, subject: form.subject } : null}
        grade={editingGrade}
        onSaved={() => fetchGrades()}
      />
    </div>
  );
}
